/**
 * OneSystem pricing on the v2 cart.
 *
 * The IV24 photo quota depends on the WHOLE cart — the most expensive eligible pack is covered first and
 * the 20 free photos can split across small packs — so no catalogue price can express it. The backend
 * prices the cart through `POST …/sessions/{id}/pricing` with the same function the payment
 * intent and placement use, and this file maps that answer back onto the cart lines, so the cart, the
 * summary and the order agree.
 */

import Big from 'big.js'

import type { CartItem, CartItemId, SessionPricedLineV2DTO } from 'models/purchaseFlowRevamp'

import { baseLineTotalOf, extraAmountOf, extraBaseAmountOf, lineTotalOf } from './lineTotal'
import type { CartExtraSnapshot, SessionPricedExtra } from './slices/cartSlice'

export interface RepricedLine {
  unitPrice: string
  baseUnitPrice: string | null
  addonsPrice: string | null
  baseAddonsPrice: string | null
  quotaCovered: boolean
}

/**
 * Folds the priced lines back onto the cart items that own them.
 *
 * An item's first owned line is its variant, priced per unit for the item's quantity; the lines after it
 * are its add-ons, ordered once for the line. The strike-through takes the public price where the
 * backend sends one and the charged price otherwise, and is kept only when the line's public total is
 * higher. `quotaCovered` is the variant line's: add-ons are never part of the quota.
 *
 * A response whose length disagrees with the request cannot be paired line by line, so it reprices
 * nothing rather than putting a price on the wrong line.
 */
export function repriceItemsFromPricing(
  items: CartItem[],
  owners: (CartItemId | null)[],
  lines: SessionPricedLineV2DTO[]
): Record<CartItemId, RepricedLine> {
  if (lines.length !== owners.length) return {}

  interface Acc { unit: Big; base: Big; addons: Big; baseAddons: Big; covered: boolean }
  const byItem = new Map<CartItemId, Acc>()
  lines.forEach((pricedLine, index) => {
    const owner = owners[index]
    if (!owner) return
    const unit = new Big(pricedLine.unitPrice.amount)
    const base = pricedLine.basePrice ? new Big(pricedLine.basePrice.amount) : unit
    const acc = byItem.get(owner)
    if (!acc) {
      byItem.set(owner, { unit, base, addons: new Big(0), baseAddons: new Big(0), covered: pricedLine.quotaCovered })
      return
    }
    acc.addons = acc.addons.plus(unit.times(pricedLine.count))
    acc.baseAddons = acc.baseAddons.plus(base.times(pricedLine.count))
  })

  const out: Record<CartItemId, RepricedLine> = {}
  for (const item of items) {
    const acc = byItem.get(item.id)
    if (!acc || item.quantity <= 0) continue
    const charged = acc.unit.times(item.quantity).plus(acc.addons)
    const publicTotal = acc.base.times(item.quantity).plus(acc.baseAddons)
    const struck = publicTotal.gt(charged)
    const hasAddons = acc.addons.gt(0) || acc.baseAddons.gt(0)
    out[item.id] = {
      unitPrice: acc.unit.toFixed(2),
      baseUnitPrice: struck ? acc.base.toFixed(2) : null,
      addonsPrice: hasAddons ? acc.addons.toFixed(2) : null,
      baseAddonsPrice: struck && hasAddons ? acc.baseAddons.toFixed(2) : null,
      quotaCovered: acc.covered,
    }
  }
  return out
}

export interface RepricedCartCounts {
  coveredCount: number
  chargedTotal: number
  discountedCount: number
  swappedCount: number
}

/**
 * What a reprice does to the cart, read before it is written back: lines the free pool covers, the variants' charged
 * total, lines at the partner price, and lines charged now that the cart showed free. Add-ons are left out; a line
 * the answer does not price keeps its cart price.
 */
export function repricedCartCounts(items: CartItem[], repriced: Record<CartItemId, RepricedLine>): RepricedCartCounts {
  const counts = { coveredCount: 0, discountedCount: 0, swappedCount: 0 }
  let chargedTotal = new Big(0)

  for (const item of items) {
    const current = item.priceSnapshot
    if (!current || !(item.quantity > 0)) continue

    const next = repriced[item.id] ?? current
    const unit = new Big(next.unitPrice)
    const base = new Big(next.baseUnitPrice ?? next.unitPrice)

    if (unit.eq(0) && base.gt(0)) counts.coveredCount++
    if (!unit.gt(0)) continue

    chargedTotal = chargedTotal.plus(unit.times(item.quantity))
    if (base.gt(unit)) counts.discountedCount++
    if (new Big(current.unitPrice).eq(0)) counts.swappedCount++
  }

  return { ...counts, chargedTotal: chargedTotal.toNumber() }
}

/**
 * Prices the instruction extras from the fee lines of the same answer — the lines `buildPricedLines`
 * sent with no owner, one per extra's `productCode` and one per `impliedFeeProductCode`, in extras order.
 * The implied fee is charged but not quoted, so it is passed over. Charged total is the unit times the
 * line's count; the public total is kept only when it is higher.
 *
 * Returns one entry per extra (`null` for one with no product code), or `null` when the fee lines do
 * not pair up with the extras by product code — a price on the wrong fee is worse than the quoted one.
 */
export function repriceExtrasFromPricing(
  extras: CartExtraSnapshot[],
  owners: (CartItemId | null)[],
  lines: SessionPricedLineV2DTO[]
): (SessionPricedExtra | null)[] | null {
  if (lines.length !== owners.length) return null

  const feeLines = lines.filter((_, index) => owners[index] === null)
  let cursor = 0
  const next = (productCode: string): SessionPricedLineV2DTO | null => {
    const feeLine = feeLines[cursor++]
    return feeLine?.productCode === productCode ? feeLine : null
  }

  const out: (SessionPricedExtra | null)[] = []
  for (const extra of extras) {
    let priced: SessionPricedExtra | null = null
    if (extra.productCode) {
      const feeLine = next(extra.productCode)
      if (!feeLine) return null
      const amount = new Big(feeLine.unitPrice.amount).times(feeLine.count)
      const base = feeLine.basePrice ? new Big(feeLine.basePrice.amount).times(feeLine.count) : null
      priced = { amount: amount.toFixed(2), baseAmount: base?.gt(amount) ? base.toFixed(2) : null }
    }
    if (extra.impliedFeeProductCode && !next(extra.impliedFeeProductCode)) return null
    out.push(priced)
  }
  return cursor === feeLines.length ? out : null
}

export interface OneSystemCartSummary {
  /** Any line priced by the OneSystem session — covered or discounted. */
  isOneSystemCart: boolean
  hasCoveredLine: boolean
  /** Public price of the CHARGED lines and the instruction extras; covered lines contribute nothing. */
  subtotal: Big
  /** `subtotal` minus what the charged lines and the extras cost. */
  partnerDiscount: Big
}

/**
 * The legacy embed's OneSystem cart block (`useEmbedOneSystemSummary`) computed from the repriced cart.
 * Only lines that were repriced carry `quotaCovered`, so a cart outside a OneSystem session reports nothing.
 * The extras are inside the cart total, so they are inside the subtotal and the partner discount too.
 */
export function oneSystemCartSummary(items: CartItem[], extras: CartExtraSnapshot[] = []): OneSystemCartSummary {
  let isOneSystemCart = false
  let hasCoveredLine = false
  let subtotal = new Big(0)
  let charged = new Big(0)

  for (const item of items) {
    const snapshot = item.priceSnapshot
    if (!snapshot || snapshot.quotaCovered === undefined) continue
    isOneSystemCart = true
    if (snapshot.quotaCovered) {
      hasCoveredLine = true
      continue
    }
    const lineCharged = lineTotalOf(snapshot, item.quantity)
    subtotal = subtotal.plus(baseLineTotalOf(snapshot, item.quantity) ?? lineCharged)
    charged = charged.plus(lineCharged)
  }

  if (isOneSystemCart) {
    for (const extra of extras) {
      const extraCharged = extraAmountOf(extra)
      subtotal = subtotal.plus(extraBaseAmountOf(extra) ?? extraCharged)
      charged = charged.plus(extraCharged)
    }
  }

  return { isOneSystemCart, hasCoveredLine, subtotal, partnerDiscount: subtotal.minus(charged) }
}
